import React, { useCallback, useMemo, useState } from "react";
import cx from "classnames";
import { Hooks, Util } from "@xt/xtbase";
import useIsDesktop from "./useIsDesktop";
import {
  buildAccountNav,
  resolveActiveNavId,
  ACCOUNT_MAIN_SECTION_KEYS,
} from "../config/accountNav";
import { AccountHeaderActionsContext } from "./accountHeaderActions";
import styles from "./index.module.less";

const { useTranslation } = Hooks;

function tr(t, key, fallback) {
  if (!t || !key) return fallback;
  const value = t(key);
  return value && value !== key ? value : fallback;
}

function RailLink({ item, active }) {
  const Icon = item.icon;
  const className = cx(styles.railLink, active && styles.railLinkActive);

  return (
    <a href={item.href} className={className}>
      <Icon className={cx(styles.railIcon, active && styles.railIconActive)} />
      <span>{item.label}</span>
    </a>
  );
}

export function AccountPageHeader({ tabKey, actions }) {
  const t = useTranslation();
  const meta = ACCOUNT_MAIN_SECTION_KEYS[tabKey];
  if (!meta) return null;

  return (
    <header className={cx(styles.pageHeader, actions && styles.pageHeaderWithActions)}>
      <div className={styles.pageHeaderText}>
        <h1 className={styles.pageTitle}>{tr(t, meta.labelKey, meta.labelFallback)}</h1>
        {meta.subtitleKey ? (
          <p className={styles.pageSubtitle}>{tr(t, meta.subtitleKey, meta.subtitleFallback)}</p>
        ) : null}
      </div>
      {actions ? <div className={styles.pageHeaderActions}>{actions}</div> : null}
    </header>
  );
}

export default function AccountShell({ tabKey, children }) {
  const t = useTranslation();
  const { isDesktop, isReady } = useIsDesktop();
  const [headerActions, setHeaderActionsState] = useState(null);
  const setHeaderActions = useCallback((node) => {
    setHeaderActionsState(node);
  }, []);
  const headerActionsCtx = useMemo(() => ({ setHeaderActions }), [setHeaderActions]);

  const navItems = useMemo(() => {
    if (!isReady || !isDesktop) return [];
    return buildAccountNav({ t, getUrl: Util.getUrl });
  }, [t, isReady, isDesktop]);
  const activeId = resolveActiveNavId(navItems, tabKey);

  if (!isReady || !isDesktop) {
    return (
      <AccountHeaderActionsContext.Provider value={headerActionsCtx}>
        {children}
      </AccountHeaderActionsContext.Provider>
    );
  }

  return (
    <AccountHeaderActionsContext.Provider value={headerActionsCtx}>
      <div className={styles.shell}>
        <div className={styles.inner}>
          <aside className={styles.aside}>
            <div className={styles.asideSticky}>
              <div className={styles.railSection}>
                <p className={styles.sectionTitle}>{tr(t, "nav.accountSection", "Account")}</p>
                <nav className={styles.rail}>
                  {navItems.map((item) => (
                    <RailLink key={item.id} item={item} active={item.id === activeId} />
                  ))}
                </nav>
              </div>
            </div>
          </aside>
          <main className={styles.main}>
            <AccountPageHeader tabKey={tabKey} actions={headerActions} />
            {children}
          </main>
        </div>
      </div>
    </AccountHeaderActionsContext.Provider>
  );
}
